import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Tile } from "@carbon/react";
import { ArrowRight, WarningAltFilled } from "@carbon/icons-react";
import { useRequesterPurchaseRequestsV2 } from "../../hooks/usePurchaseRequestsV2";
import {
  PurchaseRequestItemState,
  PurchaseRequestStatus,
  PurchaseRequestV2,
} from "../../types";

type AttentionEntry = {
  request: PurchaseRequestV2;
  prRejected: boolean;
  rejectedItemCount: number;
};

/**
 * Requester home "Needs attention" section: surfaces the requester's own purchase requests that
 * were rejected — either the whole PR (`overallStatus === REJECTED`) or individual items still
 * flagged REJECTED inside an otherwise-active PR (the roll-up can report IN_APPROVAL when some
 * lines are still pending, so a per-item scan is needed). Each card deep-links to that PR's detail,
 * where the requester can fix the item(s) and resend. Runs its own page-0 query so it stays
 * independent of the list table's current page/status filter.
 */
const RequesterAttentionCards = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Scan a generous first page of the requester's own requests, independent of the
  // table below. "ALL" so partially-rejected PRs (overallStatus IN_APPROVAL) are included.
  const { data } = useRequesterPurchaseRequestsV2(0, "ALL", true, 50);

  const entries = useMemo<AttentionEntry[]>(() => {
    const content = data?.content ?? [];
    return content
      .map((request) => {
        const rejectedItemCount = (request.items ?? []).filter(
          (item) => !item.deleted && item.state === PurchaseRequestItemState.REJECTED
        ).length;
        const prRejected =
          request.overallStatus === PurchaseRequestStatus.REJECTED;
        return { request, prRejected, rejectedItemCount };
      })
      .filter((e) => e.prRejected || e.rejectedItemCount > 0);
  }, [data]);

  if (entries.length === 0) return null;

  return (
    <section className="pr-attention" aria-label={t("purchaseRequests.attention.title")}>
      <div className="pr-attention__header">
        <WarningAltFilled size={16} className="pr-attention__header-icon" />
        <h2 className="pr-attention__title">
          {t("purchaseRequests.attention.title")}
        </h2>
      </div>
      <div className="pr-attention__grid">
        {entries.map(({ request, prRejected, rejectedItemCount }) => (
          <Tile key={request.id} className="pr-attention__card">
            <p className="pr-attention__code">
              {request.purchaseRequestCode || "-"}
            </p>
            {request.name ? (
              <p className="pr-attention__name">{request.name}</p>
            ) : null}
            <p className="pr-attention__reason">
              {prRejected
                ? t("purchaseRequests.attention.prRejected")
                : t("purchaseRequests.attention.itemsRejected", {
                    count: rejectedItemCount,
                  })}
            </p>
            {prRejected && request.rejectedMessage ? (
              <p className="pr-attention__message">{request.rejectedMessage}</p>
            ) : null}
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={ArrowRight}
              onClick={() => navigate(`/purchase-requests/${request.id}`)}
              className="pr-attention__cta"
            >
              {t("purchaseRequests.attention.review")}
            </Button>
          </Tile>
        ))}
      </div>
    </section>
  );
};

export default RequesterAttentionCards;
